import { completeBooking, readDate, readTimes } from './booking'
import { jsonIn, type BookingParts, type ReadResult } from './read'
import { withoutReplies } from './replies'
import { LABEL_WORDS, plainWord, ROUTE_SEPARATOR } from './ticketWords'

/**
 * The check for a booking's critical fields (docs/screenshot-cleanup/PLAN.md, phase 2: "0 silent errors"). A second, blind look at the
 * screenshots copies every line that holds a date, time, place, seat, reference, name or deadline, without seeing what the reading
 * returned; code then confirms each field against those lines, dates and times by meaning. Two independent reads that agree are
 * evidence; one read shown its own answer is not. A reference found inside a decoded code is confirmed by the device with no model
 * at all. A field neither confirms is kept and marked to be checked, never hidden or corrected: the error that matters is the one
 * nobody is told about.
 */
export const VERIFY_VERSION = 'verify-v2'

/**
 * The critical fields of a booking, by a stable name ("when", "times.0", "seats.1"), with the value the reading returned. A time or
 * deadline with no date or clock time in it ("20 minutes before departure") is information, not a moment to get wrong, and is left out.
 */
export function criticalFields(booking: BookingParts, capturedAt = Date.now()): Record<string, string> {
  const fields: Record<string, string> = {}
  const moment = (text: string) => readTimes(text).length > 0 || readDate(text, capturedAt) !== null
  if (booking.when) fields.when = booking.when
  booking.times.forEach((entry, i) => { if (moment(entry.text)) fields[`times.${i}`] = entry.text })
  if (booking.where) fields.where = booking.where
  booking.seats.forEach((seat, i) => { fields[`seats.${i}`] = seat })
  if (booking.reference) fields.reference = booking.reference
  booking.deadlines.forEach((entry, i) => { if (moment(entry.text)) fields[`deadlines.${i}`] = entry.text })
  if (booking.people) fields.people = booking.people
  return fields
}

export function buildVerifyPrompt(): string {
  return [
    'Copy, exactly as shown, every line of these phone screenshots that holds a date, a time, a place, address, route or airport code, a seat,',
    'row, block, section, stand, level, entrance, gate, terminal, zone, boarding group, platform, coach, cabin, deck or table, a booking, order',
    'or confirmation reference, a person\'s name, a number of guests, or a deadline. One entry per line, in reading order, with the label shown',
    'beside it (for example "Doors 19:00", "Zone 7"). The screenshots may be in English, German, French, Spanish or Italian: copy every line in',
    'its own language. Copy only: never translate, correct, complete or work anything out, and never copy the content of a QR code or barcode.',
    'Return JSON only: {"lines":["..."]}',
  ].join('\n')
}

/** The copied lines; anything malformed is dropped. */
export function parseVerify(content: string): string[] {
  const body = jsonIn(content) as Record<string, unknown>
  return (Array.isArray(body.lines) ? body.lines : []).flatMap(line => (typeof line === 'string' && line.trim() ? [line.trim().slice(0, 400)] : [])).slice(0, 80)
}

const flat = (text: string) => plainWord(text).replace(/[^\p{L}\p{N}]+/gu, '')
// A value without its label words in any supported language: "Sitzplatz 14C" against a line showing "14C".
const core = (text: string) => flat(plainWord(text).replace(LABEL_WORDS, ' '))

/**
 * Whether the copied lines show the value: one line holds it as written, or under another label; or a value the reading joined from
 * several lines ("BER to TLV", "Harbour Hall · Entrance B") shows every part on some line; or, for dates and times, some line has the
 * same date and every clock time appears on a line (a boarding pass puts the date and the departure on different lines).
 */
export function confirms(value: string, lines: readonly string[], capturedAt: number): boolean {
  if (confirmsWhole(value, lines, capturedAt)) return true
  const parts = value.split(/\s*[·|•,/]\s*/).flatMap(part => part.split(ROUTE_SEPARATOR)).map(part => part.trim()).filter(part => flat(part).length >= 2)
  return parts.length > 1 && parts.every(part => confirmsWhole(part, lines, capturedAt) || coveredByLines(part, lines))
}

/** Every word of a part is in some line that appears whole inside it: "BER Terminal 1" from the lines "BER" and "Terminal 1". */
function coveredByLines(part: string, lines: readonly string[]): boolean {
  const words = plainWord(part).match(/[\p{L}\p{N}]+/gu) ?? []
  const covered = new Set<number>()
  for (const line of lines) {
    const inLine = plainWord(line).match(/[\p{L}\p{N}]+/gu) ?? []
    if (!inLine.length || inLine.join('').length < 2) continue
    for (let start = 0; start + inLine.length <= words.length; start++) {
      if (inLine.every((word, i) => words[start + i] === word)) inLine.forEach((_, i) => covered.add(start + i))
    }
  }
  return words.length > 0 && covered.size === words.length
}

function confirmsWhole(value: string, lines: readonly string[], capturedAt: number): boolean {
  const wanted = flat(value)
  if (!wanted) return false
  if (lines.some(line => flat(line).includes(wanted))) return true
  // The same value under a different label: "Seat 14C" against a line showing "14C", "Order #HH-48213" against "HH-48213". A code
  // of letters alone ("TLV", "C") counts when a line is exactly that code.
  const bare = core(value)
  if (bare.length >= 2 && /\d/.test(bare) && lines.some(line => flat(line).includes(bare))) return true
  if (bare.length >= 1 && lines.some(line => core(line) === bare)) return true
  const date = readDate(value, capturedAt), times = readTimes(value).map(time => time.minutes)
  if (!date && !times.length) return false
  const sameDate = !date || lines.some(line => { const seen = readDate(line, capturedAt); return seen !== null && seen.y === date.y && seen.m === date.m && seen.d === date.d })
  const seenTimes = new Set(lines.flatMap(line => readTimes(line).map(time => time.minutes)))
  return sameDate && times.every(time => seenTimes.has(time))
}

/** Field names the check left unconfirmed; a reference inside a decoded code needs no copied line. */
export function unconfirmed(fields: Record<string, string>, lines: readonly string[], codeTexts: readonly string[], capturedAt: number): string[] {
  const codes = codeTexts.map(flat)
  return Object.entries(fields).filter(([name, value]) => {
    if (name === 'reference' && flat(value).length >= 4 && codes.some(code => code.includes(flat(value)))) return false
    return !confirms(value, lines, capturedAt)
  }).map(([name]) => name)
}

/**
 * Lines the check saw that hold a date or time no field accounts for ("Free cancellation until Thu 22 Oct, 19:30" when the reading
 * left the deadline out). They are shown on the card as they are, so a miss is visible rather than silent.
 */
export function missedLines(booking: BookingParts, lines: readonly string[], capturedAt: number): string[] {
  const held = [booking.when, ...booking.times.map(entry => entry.text), ...booking.deadlines.map(entry => entry.text)].filter((text): text is string => Boolean(text))
  const seen = new Set<string>()
  return lines.filter(line => {
    const date = readDate(line, capturedAt), times = readTimes(line).map(time => time.minutes)
    if (!date && !times.length) return false
    // A line with a held date and one of its times is the booking's own line (with doors beside the show, say); a line with only
    // times is covered when all are held; a line with another date (a cancellation deadline) is not covered.
    const covered = held.some(text => {
      const other = readDate(text, capturedAt), otherTimes = new Set(readTimes(text).map(time => time.minutes))
      if (date) return other !== null && other.y === date.y && other.m === date.m && other.d === date.d && (!times.length || times.some(time => otherTimes.has(time)))
      return times.every(time => otherTimes.has(time))
    })
    const key = flat(line)
    if (covered || seen.has(key)) return false
    seen.add(key)
    return true
  }).slice(0, 6)
}

export interface BookingMarks { unconfirmed: string[]; alsoShown: string[] }

/**
 * A run's reading with its bookings checked again by today's code against the lines its check copied, so a better rule (a route
 * confirmed end by end, a label in German) clears a mark on a note unpacked before it, without asking the model again.
 */
export interface CheckedRun {
  read?: { result?: ReadResult | null } | null
  verification?: { checks: Array<{ item: number; lines: string[]; error: string | null }> } | null
  prep?: { images: Array<{ codes: Array<{ text: string }> }> } | null
}

/** A reading with each booking completed from its item's own text (booking.ts `completeBooking`), as a newer reading would be. */
export function completed(result: ReadResult, capturedAt: number): ReadResult {
  return { ...result, items: result.items.map(item => (item.booking ? { ...item, booking: completeBooking(item.booking, item.text, capturedAt) } : item)) }
}

/** The checked reading as Unpack shows it: replies under a post left out (replies.ts), after the checks, which count items by position. */
export function rechecked(run: CheckedRun, capturedAt: number): ReadResult | null {
  const result = checkedReading(run, capturedAt)
  return result && withoutReplies(result)
}

function checkedReading(run: CheckedRun, capturedAt: number): ReadResult | null {
  const result = run.read?.result ? completed(run.read.result, capturedAt) : null
  const checks = run.verification?.checks ?? []
  if (!result || !checks.length) return result
  const codeTexts = (run.prep?.images ?? []).flatMap(image => image.codes.map(code => code.text))
  // A start time only the check copied, beside the date the ticket labels as the start, completes the booking first.
  const items = result.items.map((item, index) => {
    const check = checks.find(candidate => candidate.item === index)
    return item.booking && check?.lines && !check.error ? { ...item, booking: completeBooking(item.booking, item.text, capturedAt, check.lines) } : item
  })
  const marks = new Map(checks.flatMap(check => {
    const booking = items[check.item]?.booking
    if (!booking || check.error || !check.lines) return []
    return [[check.item, { unconfirmed: unconfirmed(criticalFields(booking, capturedAt), check.lines, codeTexts, capturedAt), alsoShown: missedLines(booking, check.lines, capturedAt) }] as [number, BookingMarks]]
  }))
  return marks.size ? withVerification({ ...result, items }, marks) : { ...result, items }
}

/** The reading with each checked item's unconfirmed fields and missed lines marked on its booking. */
export function withVerification(result: ReadResult, marks: ReadonlyMap<number, BookingMarks>): ReadResult {
  return {
    ...result,
    items: result.items.map((item, index) => {
      const mark = marks.get(index)
      return item.booking && mark ? { ...item, booking: { ...item.booking, unconfirmed: mark.unconfirmed, alsoShown: mark.alsoShown } } : item
    }),
  }
}
